import React, { useEffect, useState } from 'react';
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Select,
    useDisclosure,
    VStack,
    SimpleGrid,
    Checkbox
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';

const InviteTeamMemberModal = ({ isOpen, onClose, type }) => {
    const userData = getUserData();
    const clinic_id = userData?.clinic_id ?? null;
    const [loading, setLoading] = useState(false);


    const validationSchema = Yup.object().shape({
        fullName: Yup.string().required("Team Member's Name is required"),
        email: Yup.string()
            .email("Invalid email address")
            .required("Team Member's Email is required"),
        role: Yup.string().required("Role is required"),
        designation: Yup.string().required("Designation is required"),
    });



    const inviteTeamMember = async (values, { resetForm }) => {
        setLoading(true);
        const payload = {
            name: values.fullName,
            email: values.email,
            role: Number(formik.values.role),
            designation: values.designation,
            clinic_id,
            user_id: userData?.id,
            locations: selectedLocations,
        };

        try {
            await createAPIEndPoint('team/send_invitation').create(payload);
            toast.success('Invitation sent successfully!');
            resetForm()
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to invite member');
        } finally {
            setLoading(false);
        }
    };


    const [roles, setRoles] = useState([]);


    useEffect(() => {
        const getRoles = async () => {
            try {
                const response = await createAPIEndPoint(`clinic_roles/get_all/${clinic_id}`).fetchAll();
                setRoles(response.data.roles);
            } catch (error) {
                toast.error(error?.response?.data?.error || 'Error fetching roles');
            }
        };
        if (isOpen) {
            getRoles();
        }
    }, [isOpen]);

    const [locations, setLocations] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);

    const fetchLocations = async () => {
        try {
            const response = await createAPIEndPoint(`clinic_locations/get_all/${clinic_id}`).fetchAll();
            const locations = response.data.locations || [];

            // Map locations to format name, city, and state
            const formattedLocations = locations.map((location) => ({
                id: location.id,
                name: `${location.location_name} (${location.city}, ${location.state})`, // Display name with city & state
            }));

            setLocations(formattedLocations);
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to fetch locations');
        }
    };

    useEffect(() => {
        if (clinic_id) {
            fetchLocations();
        }
    }, [isOpen, clinic_id]);

    const handleLocationChange = (locationId) => {
        setSelectedLocations((prev) =>
            prev.includes(locationId)
                ? prev.filter((id) => id !== locationId)
                : [...prev, locationId],
        );
    };




    const formik = useFormik({
        initialValues: {
            fullName: '',
            email: '',
            role: '',
            designation: '',
        },
        validationSchema,
        onSubmit: inviteTeamMember,
    });


    // Find the role object based on the selected id (converted to string)
    const selectedRole = roles.find(role => String(role.id) === formik.values.role);

    // Determine if the role name is 'staff' or 'manager'
    const showLocations =
        selectedRole &&
        (selectedRole.role_name.toLowerCase() === "staff" ||
            selectedRole.role_name.toLowerCase() === "manager");


    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="16px" pb="16px">
                <ModalHeader color="brand.500">
                    {type === "edit" ? "Edit Team Member" : "Invite Team Member"}
                </ModalHeader>
                <ModalCloseButton />
                <form onSubmit={formik.handleSubmit}>
                    <ModalBody maxHeight="70vh" overflowY="auto" pb={2}>
                        {/* Team Member's Name */}
                        <FormControl isInvalid={formik.touched.fullName && formik.errors.fullName} mb={2}>
                            <FormLabel>Team Member's Name</FormLabel>
                            <Input
                                name="fullName"
                                variant="auth"
                                fontSize="sm"
                                fontWeight="500"
                                placeholder="Enter name"
                                value={formik.values.fullName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.fullName && formik.errors.fullName && (
                                <Text color="red.500" fontSize="sm">{formik.errors.fullName}</Text>
                            )}
                        </FormControl>

                        {/* Team Member's Email */}
                        <FormControl isInvalid={formik.touched.email && formik.errors.email} mb={2}>
                            <FormLabel>Team Member's Email</FormLabel>
                            <Input
                                name="email"
                                variant="auth"
                                fontSize="sm"
                                fontWeight="500"
                                placeholder="Enter email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <Text color="red.500" fontSize="sm">{formik.errors.email}</Text>
                            )}
                        </FormControl>

                        {/* Designation */}
                        <FormControl isInvalid={formik.touched.designation && formik.errors.designation} mb={2}>
                            <FormLabel>Designation</FormLabel>
                            <Input
                                name="designation"
                                variant="auth"
                                fontSize="sm"
                                fontWeight="500"
                                placeholder="Enter designation"
                                value={formik.values.designation}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.designation && formik.errors.designation && (
                                <Text color="red.500" fontSize="sm">{formik.errors.designation}</Text>
                            )}
                        </FormControl>

                        {/* Role Selection */}

                        <FormControl
                            isInvalid={
                                formik.touched.role && formik.errors.role
                            }
                            mb={2}
                        >
                            <FormLabel mb={0}>Select Role</FormLabel>
                            <Select
                                name="role"
                                variant="auth"
                                fontSize="sm"
                                fontWeight="500"
                                placeholder="Select role"
                                value={formik.values.role}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isDisabled={loading}
                                textTransform="capitalize"
                            >
                                {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.role_name}
                                    </option>
                                ))}
                            </Select>
                            {formik.touched.role &&
                                formik.errors.role && (
                                    <Text color="red.500" fontSize="sm">
                                        {formik.errors.role}
                                    </Text>
                                )}
                        </FormControl>

                        {showLocations && (
                            <FormControl>
                                <FormLabel>Select Locations</FormLabel>
                                <VStack align="start">
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                        {locations.map((location) => (
                                            <Checkbox
                                                key={location.id}
                                                isChecked={selectedLocations.includes(location.id)}
                                                onChange={() => handleLocationChange(location.id)}
                                            >
                                                <span style={{ fontSize: '.85rem' }}>{location.name}</span>
                                            </Checkbox>
                                        ))}
                                    </SimpleGrid>
                                </VStack>
                            </FormControl>
                        )}
                    </ModalBody>
                    <ModalFooter pt={0}>
                        <Button type="submit" colorScheme="brand" w="100%" mt={4} isLoading={formik.isSubmitting}>
                            {type === "edit" ? "Edit Invitation" : "Send Invitation"}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};

export default InviteTeamMemberModal;
